import React, { useState } from "react";
import { Plus, Search, Users, UserCheck, AlertCircle, UserMinus, Filter, X } from "lucide-react";
import { useEmployees } from "../hooks/useEmployees";
import { EmployeeTable } from "../components/onboarding/EmployeeTable";
import { EmployeeFormModal } from "../components/onboarding/EmployeeFormModal";
import { EmployeeDetailModal } from "../components/onboarding/EmployeeDetailModal";
import { StatusConfirmModal } from "../components/StatusConfirmModal";
import { DeleteConfirmModal } from "../components/DeleteConfirmModal";
import { PaySalaryModal } from "../components/onboarding/PaySalaryModal";
import { HRStatCard } from "../components/HRStatCard";
import { Pagination } from "@/components/ui";
import { DEPARTMENTS, EMPLOYMENT_STATUS, EMPLOYMENT_STATUS_LABELS, EMPLOYMENT_TYPE } from "../constants/hrConstants";

export function EmployeeOnboardingScreen() {
  const {
    employees, stats, search, setSearch,
    filterDept, setFilterDept, filterStatus, setFilterStatus,
    filterType, setFilterType,
    isFormOpen, editingEmployee, viewingEmployee, setViewingEmployee,
    formData, formErrors, isSaving,
    openCreate, openEdit, closeForm, updateField, save,
    toggleEmployeeStatus, deleteEmployee,
  } = useEmployees();

  const [statusConfirmTarget, setStatusConfirmTarget] = useState(null); // holds entire employee object
  const [paySalaryTarget, setPaySalaryTarget] = useState(null); // holds entire employee object for payment
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState(null); // holds employee ID
  const [searchVisible, setSearchVisible] = useState(false);
  const [page, setPage] = useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [search, filterDept, filterStatus, filterType]);

  const PAGE_SIZE = 10;
  const totalPages = Math.ceil(employees.length / PAGE_SIZE) || 1;
  const paginatedEmployees = employees.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleDeleteClick = (id) => {
    setDeleteConfirmTarget(id);
  };

  const executeDelete = async () => {
    if (deleteConfirmTarget) {
      await deleteEmployee(deleteConfirmTarget);
      setDeleteConfirmTarget(null);
    }
  };

  const handleToggleStatusClick = (id) => {
    const emp = employees.find((e) => e._id === id || e.id === id);
    if (emp) {
      setStatusConfirmTarget(emp);
    }
  };

  const executeStatusToggle = () => {
    if (statusConfirmTarget) {
      toggleEmployeeStatus(statusConfirmTarget.id);
      setStatusConfirmTarget(null);
    }
  };

  return (
    <>
      <div className="space-y-6 animate-in fade-in duration-400 bg-slate-50">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
        <HRStatCard label="Total Employees" value={stats.total} icon={Users} color="blue" />
        <HRStatCard label="Active" value={stats.active} icon={UserCheck} color="emerald" />
        <HRStatCard label="On Probation" value={stats.probation} icon={AlertCircle} color="amber" />
        <HRStatCard label="Inactive" value={stats.inactive} icon={UserMinus} color="slate" />
      </div>

      {/* Control Panel */}
      <div className="card p-2 sm:p-3 flex flex-wrap items-center gap-2 bg-white shadow-sm border border-slate-100 animate-in fade-in slide-in-from-top-1 rounded-md">

        {/* Search Toggle Button */}
        <button
          onClick={() => { setSearchVisible(v => !v); if (searchVisible) setSearch(''); }}
          className={`p-2 h-9 w-9 flex items-center justify-center rounded-md border transition-all ${
            searchVisible
              ? 'bg-blue-600 border-blue-600 text-white shadow-sm shadow-blue-200'
              : 'border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-blue-600 hover:border-blue-300'
          }`}
          title="Search"
        >
          <Search size={16} />
        </button>

        {/* Dept Dropdown */}
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 text-slate-600 min-w-[140px] max-w-[180px] w-auto"
            value={filterDept} onChange={(e) => setFilterDept(e.target.value)}
          >
            <option value="">All Departments</option>
            {DEPARTMENTS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>

        {/* Status Dropdown */}
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0 hidden sm:block">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 text-slate-600 min-w-[120px] max-w-[160px] w-auto"
            value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Status</option>
            {Object.entries(EMPLOYMENT_STATUS).map(([, v]) => (
              <option key={v} value={v}>{EMPLOYMENT_STATUS_LABELS[v]}</option>
            ))}
          </select>
        </div>

        {/* Type Dropdown */}
        <div className="bg-slate-50/50 p-1 rounded-md border border-slate-200/60 shrink-0 hidden sm:block">
          <select
            className="text-[13px] font-medium bg-white border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 text-slate-600 min-w-[120px] max-w-[160px] w-auto"
            value={filterType} onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All Types</option>
            {Object.entries(EMPLOYMENT_TYPE).map(([, v]) => (
              <option key={v} value={v}>{v.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}</option>
            ))}
          </select>
        </div>

        {/* Add Employee Button */}
        <div className="ml-auto">
          <button
            onClick={openCreate}
            className="h-9 flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-semibold text-white bg-blue-600 hover:bg-blue-700 shadow-sm shadow-blue-200 hover:scale-105 active:scale-95 transition-all whitespace-nowrap"
          >
            <Plus size={16} /> Add Employee
          </button>
        </div>
      </div>

      {/* Search Overlay */}
      {searchVisible && (
        <div className="card p-3 flex items-center gap-3 bg-white shadow-sm border border-slate-100 rounded-md animate-in fade-in slide-in-from-top-1">
          <Search size={16} className="text-slate-400 shrink-0" />
          <input
            type="text"
            autoFocus
            className="flex-1 bg-transparent text-sm text-slate-700 placeholder:text-slate-400 outline-none"
            placeholder="Search by name, email, ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button onClick={() => setSearch('')} className="text-slate-400 hover:text-slate-600">
              <X size={15} />
            </button>
          )}
        </div>
      )}

      {/* Table & Pagination Wrapper */}
      <div className="flex flex-col">
        <EmployeeTable
          employees={paginatedEmployees}
          onEdit={openEdit}
          onView={setViewingEmployee}
          onToggleStatus={handleToggleStatusClick}
          onPay={setPaySalaryTarget}
          onDelete={handleDeleteClick}
        />
        {totalPages > 1 && (
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        )}
      </div>
      </div>

      {/* Modals */}
      <EmployeeFormModal
        isOpen={isFormOpen}
        onClose={closeForm}
        formData={formData}
        formErrors={formErrors}
        onFieldChange={updateField}
        onSubmit={save}
        isEditing={Boolean(editingEmployee)}
        isSaving={isSaving}
      />
      <EmployeeDetailModal
        employee={viewingEmployee}
        onClose={() => setViewingEmployee(null)}
      />
      <StatusConfirmModal
        isOpen={Boolean(statusConfirmTarget)}
        onClose={() => setStatusConfirmTarget(null)}
        onConfirm={executeStatusToggle}
        employeeName={statusConfirmTarget?.name}
        currentStatus={statusConfirmTarget?.status}
      />
      <PaySalaryModal
        isOpen={Boolean(paySalaryTarget)}
        onClose={() => setPaySalaryTarget(null)}
        employee={paySalaryTarget}
      />
      <DeleteConfirmModal
        isOpen={Boolean(deleteConfirmTarget)}
        onClose={() => setDeleteConfirmTarget(null)}
        onConfirm={executeDelete}
        title="Delete Employee"
        message="Are you sure you want to permanently delete this employee? This action cannot be undone."
      />
    </>
  );
}
